import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Loader2, Printer } from 'lucide-react';
import '../../styles/ali-sons-invoice-styles.css';

const AliSonsInvoiceWithDownload = ({ formData }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0.00';
    const value = parseFloat(num);
    const isNegative = value < 0;
    const formattedNum = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return isNegative ? `(${formattedNum})` : formattedNum;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateTotalAmount = () => {
    try {
      const landscapeItems = formData.details.billItems.landscape;
      const swimmingItems = formData.details.billItems.swimming;
      
      const landscapeTotal = Object.values(landscapeItems)
        .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      const swimmingTotal = Object.values(swimmingItems)
        .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      
      return landscapeTotal + swimmingTotal;
    } catch (error) {
      console.error('Error calculating total:', error);
      return 0;
    }
  };

  const downloadPDF = async () => {
    try {
      setIsGenerating(true);
      const invoiceElement = document.getElementById('invoice-container');
      
      const canvas = await html2canvas(invoiceElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        backgroundColor: '#ffffff'
      });
      
      const imgWidth = 212;
      const pageHeight = 298;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 1.0),
        'JPEG',
        0,
        0,
        imgWidth,
        imgHeight
      );
      
      const filename = `Invoice_${formData.header?.taxInvoiceRef || 'document'}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const totalAmount = calculateTotalAmount();
  const vatAmount = totalAmount * 0.05;
  const totalWithVat = totalAmount + vatAmount;
  const thisPeriodsWD = calculateTotalAmount() - formData.details.totalWDLastPeriod;
  const advance = thisPeriodsWD * -0.1;
  const retention = thisPeriodsWD * -0.1;
  const finalTotal = (thisPeriodsWD * 0.8);
  const vatOnFinal = finalTotal * 0.05;

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50 py-8">
      {/* Action Buttons */}
      <div className="flex gap-4 mb-4 print:hidden">
        <button 
          onClick={downloadPDF}
          disabled={isGenerating}
          className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-blue-300 flex items-center gap-2 text-[11px]"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating PDF...
            </>
          ) : (
            'Download Invoice PDF'
          )}
        </button>
        <button 
          onClick={printInvoice}
          className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2 text-[11px]"
        >
          <Printer className="h-4 w-4" />
          Print Invoice
        </button>
      </div>

      <div id="invoice-container" className="w-full max-w-[210.06mm] bg-white min-h-screen print:w-[210mm] print:min-h-[297mm]">
        <div className="p-[10mm] pt-[5mm]">
          {/* Header Section */}
          <div className="flex flex-col gap-4">
            <div className="relative flex justify-center">
              {/* Left Side */}
              <div className="absolute left-0 text-left">
                <div className="text-blue-500 text-[11px] font-bold">TAX INVOICE</div>
                <div className="text-red-500 font-bold">S.N.: {formData.header?.serialNumber}...2024</div>
                <div className="font-bold">Invoice Ref: {formData.header?.taxInvoiceRef}</div>
                <div className="font-bold">Date: {formatDate(formData.header?.taxInvoiceDate)}</div>
              </div>
              
              {/* Center */}
              <div className="text-left mx-auto">
                <div className="text-blue-500 font-bold">Project: Phase 1, Dana Bay</div>
                <div className="text-blue-500 font-bold">Sub-Contract Ref: 172SCA/065/23/FAW</div>
                <div className="text-blue-500 font-bold">Dated November 25, 2023</div>
                <div className="text-blue-500 font-bold">Contract Value: AED 9,214,000.00</div>
              </div>
            </div>

            {/* Company Details */}
            <div className="grid grid-cols-2 gap-x-4">
              <div className="font-bold">
                <div>From: Phoenician Technical Services LLC.</div>
                <div>P.O Box: 377972, Dubai, UAE</div>
                <div>Central Plaza, Office 399, DIP-1</div>
                <div>Tel: 04-8829484 Fax: 04-8829585</div>
                <div>Email: mjawfer@phoenician-uae.com</div>
                <div className="text-green-600">TRN:100058522200003</div>
              </div>

              <div className="font-bold">
                <div>To: M/s. Ali & Sons Contracting Company</div>
                <div>Owned by Ali and Sons Holding-Sole Proprietorship LLC</div>
                <div>P.O.Box: 2153, Abu Dhabi</div>
                <div>Tel: +97125546611 | F: +97125544788</div>
                <div>Email: jithin.joseph@ali-sons.com</div>
                <div className="text-green-600">TRN:100244272900003</div>
              </div>
            </div>
          </div>

          {/* Main Table */}
          <table className="w-full border-collapse border-l border-r border-t border-black text-[11px] leading-tight mt-2">
            <thead>
              <tr>
                <th className="border border-black p-0.5 text-center w-[35%] font-bold">Description</th>
                <th className="border border-black p-0.5 text-center w-[6%] font-bold">Unit</th>
                <th className="border border-black p-0.5 text-center w-[6%] font-bold">Qty</th>
                <th className="border border-black p-0.5 text-center w-[12%] font-bold">Rate (AED)</th>
                <th className="border border-black p-0.5 text-center w-[12%] font-bold">Amount (AED)</th>
                <th className="border border-black p-0.5 text-center w-[6%] font-bold bg-[rgb(230,255,255)]">Vat %</th>
                <th className="border border-black p-0.5 text-center w-[12%] font-bold bg-[rgb(230,255,255)]">Vat Amount (AED)</th>
                <th className="border border-black p-0.5 text-center w-[16%] font-bold">Total Amount (AED)</th>
              </tr>
            </thead>
            <tbody className="[&>tr>td]:border-l [&>tr>td]:border-black">
              {/* Landscaping Works Section */}
              <tr>
                <td className="p-1 font-bold">
                  Complete Landscaping works (RFI 572/JCA/065/23/FAW)
                  <br />
                  Bill No. 01 - Preliminaries & General Requirements
                </td>
                <td className="p-1 text-center font-bold">Item</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.preliminaries.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.preliminaries.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.preliminaries.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.landscape.preliminaries.amount * 1.05)}
                </td>
              </tr>

              <tr>
                <td className="p-1 font-bold">Bill No. 02 - Landscaping Works</td>
                <td className="p-1 text-center font-bold">Item</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.landscapingWorks.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.landscapingWorks.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.landscapingWorks.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.landscape.landscapingWorks.amount * 1.05)}
                </td>
              </tr>

              <tr>
                <td className="p-1 font-bold">Bill No. 03 - Tenderer's Adjustments for Bill No. 02</td>
                <td className="p-1 text-center font-bold">Item</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.tenderersAdjustments.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.tenderersAdjustments.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.tenderersAdjustments.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.landscape.tenderersAdjustments.amount * 1.05)}
                </td>
              </tr>

              <tr>
                <td className="p-1 font-bold">Bill No. 04 - Provisional Sums</td>
                <td className="p-1 text-center font-bold">Item</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.ProvisionalSums.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.ProvisionalSums.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.landscape.ProvisionalSums.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.landscape.ProvisionalSums.amount * 1.05)}
                </td>
              </tr>

              {/* Swimming Pool Works Section */}
              <tr>
                <td className="p-1 font-bold">
                  Swimming Pool works (372/JCA/081/23/FAW)
                  <br />
                  Bill No. 01 - Preliminaries General Requirements
                </td>
                <td className="p-1 text-center font-bold">Nos</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.preliminaries.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.preliminaries.amount)}
                </td><td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.preliminaries.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.swimming.preliminaries.amount * 1.05)}
                </td>
              </tr>

              <tr>
                <td className="p-1 font-bold">Bill No. 02 - Swimming Pool MEP Works</td>
                <td className="p-1 text-center font-bold">Nos</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.mepWorks.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.mepWorks.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.mepWorks.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.swimming.mepWorks.amount * 1.05)}
                </td>
              </tr>

              <tr>
                <td className="p-1 font-bold">Bill No. 03 - Swimming Pool Civil & Finishing Works</td>
                <td className="p-1 text-center font-bold">Nos</td>
                <td className="p-1 text-center font-bold">1.00</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.civilWorks.amount)}
                </td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.civilWorks.amount)}
                </td>
                <td className="p-1 text-center font-bold">5%</td>
                <td className="p-1 text-right font-bold">
                  AED {formatNumber(formData.details.billItems.swimming.civilWorks.amount * 0.05)}
                </td>
                <td className="p-1 text-right font-bold border-r">
                  AED {formatNumber(formData.details.billItems.swimming.civilWorks.amount * 1.05)}
                </td>
              </tr>

              {/* Total row */}
              <tr>
                <td colSpan="4"></td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(totalAmount)}
                </td>
                <td className="text-center">5%</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(vatAmount)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(totalWithVat)}
                </td>
              </tr>

              {/* Value headers - starting calculation section */}
              <tr>
                <td colSpan="6"></td>
                <td className="p-0.5 text-right pr-2 font-bold italic">Value(AED)</td>
                <td className="p-0.5 text-right pr-2 font-bold italic bg-[rgb(230,255,253)] border-r">Vat 5% (AED)</td>
              </tr>

              {/* Total WD to Date */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">Total WD to Date</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(totalAmount)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(vatAmount)}
                </td>
              </tr>

              {/* Total WD Last Period */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">Total WD Last Period</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(formData.details.totalWDLastPeriod)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(formData.details.totalWDLastPeriod * 0.05)}
                </td>
              </tr>

              {/* WD This Period */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">WD This Period</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(thisPeriodsWD)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(thisPeriodsWD * 0.05)}
                </td>
              </tr>

              {/* Advance 10% */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">Advance 10%</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(advance)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(advance * 0.05)}
                </td>
              </tr>

              {/* Retention 10% */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">Retention 10%</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(retention)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(retention * 0.05)}
                </td>
              </tr>

              {/* Total */}
              <tr>
                <td colSpan="6" className="text-right pr-2 font-bold italic">Total</td>
                <td className="text-right pr-2 font-bold">
                  AED {formatNumber(finalTotal)}
                </td>
                <td className="text-right pr-2 font-bold border-r">
                  AED {formatNumber(vatOnFinal)}
                </td>
              </tr>

              {/* Summary Section */}
              <tr>
                <td colSpan="8" className="relative border-t-2 border-black p-2">
                  <div className="absolute w-[1px] bg-black left-1/2 top-0 bottom-0" style={{ height: 'calc(100% + 120px)' }}></div>
                  <div className="w-1/2 ml-auto text-center">
                    <div className="relative">
                      <span className="relative bg-white px-4 font-bold">summary</span>
                    </div>
                  </div>
                </td>
              </tr>

              {/* Summary Rows */}
              <tr>
                <td colSpan="4"></td>
                <td colSpan="2" className="text-right pr-2 font-bold italic">Gross Value (in AED)</td>
                <td colSpan="2" className="text-right pr-4 border-r">
                  AED {formatNumber(finalTotal)}
                </td>
              </tr>

              <tr>
                <td colSpan="4"></td>
                <td colSpan="2" className="text-right pr-2 font-bold italic bg-[rgb(230,255,255)]">Add-VAT 5% (in AED)</td>
                <td colSpan="2" className="text-right pr-4 border-r">
                  AED {formatNumber(vatOnFinal)}
                </td>
              </tr>

              <tr>
                <td colSpan="4"></td>
                <td colSpan="2" className="text-right pr-2 font-bold italic">Less-Other Deduction</td>
                <td colSpan="2" className="text-right pr-4 border-r">
                  {formData.deductions.otherDeduction.checked 
                    ? `AED ${formatNumber(formData.deductions.otherDeduction.amount)}`
                    : '-'}
                </td>
              </tr>

              <tr>
                <td colSpan="4"></td>
                <td colSpan="2" className="text-right pr-2 font-bold italic">Less - Hold-PG</td>
                <td colSpan="2" className="text-right pr-4 border-r">-</td>
              </tr>

              <tr>
                <td colSpan="4"></td>
                <td colSpan="2" className="text-right pr-2 font-bold italic">Net Value (in AED)</td>
                <td colSpan="2" className="text-right pr-4 font-bold border-r">
                  AED {formatNumber(
                    finalTotal + vatOnFinal - 
                    (formData.deductions.otherDeduction.checked ? formData.deductions.otherDeduction.amount : 0)
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Bank Details */}
          <div className="mt-4 text-[11px] font-bold">
            <div className="text-left">
              <div className="font-bold underline">Bank Details:</div>
              <div>Bank Name & Branch: INVEST BANK, Dubai, U.A.E.</div>
              <div>Account Number: {formData.bankDetails.accountNumber}</div>
              <div>IBAN: {formData.bankDetails.iban}</div>
              <div>SWIFT: {formData.bankDetails.swift}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AliSonsInvoiceWithDownload;